import React, { useState } from "react";
import { format } from "date-fns";
import clsx from "clsx";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import SearchImage from "../images/Search.png";
import { resolveMediaUrl } from "../../../utilities/mediaHelper";

const getOutletStatusClass = (status) => {
  switch (status) {
    case "Outlet Open":
      return "open";
    case "Outlet Closed":
      return "closed";
    case "Outlet Not Found":
      return "not-found";
    default:
      return "";
  }
};

const getQrStatusClass = (status) => {
  switch (status) {
    case "QR Matched":
      return "matched";
    case "QR Mismatched":
      return "mismatched";
    case "QR Invalid":
      return "invalid";
    case "QR Not Found":
      return "qr-not-found";
    default:
      return "";
  }
};

const formattedTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return format(date, "MMMM d, yyyy; h:mm a");
};

const renderStatusIcon = (statusClass) => {
  const isPositiveStatus = statusClass === "open" || statusClass === "matched";

  return (
    <div className={`status-icon-container ${statusClass}`}>
      {isPositiveStatus ? (
        <CheckIcon className={`status-icon ${statusClass}`} />
      ) : (
        <CloseIcon className={`status-icon ${statusClass}`} />
      )}
    </div>
  );
};

const VisitCallImages = ({
  icon,
  calls,
  loading = false,
  error = false,
  errorMessage = "",
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleImageIndexChange = (index) => {
    setImageLoading(true);
    setImageError(false);
    setSelectedImageIndex(index);
  };

  if (loading) {
    return (
      <div className="outlet-image-section">
        <div className="outlet-image-section__header">
          <div className="image-navigation">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="skeleton skeleton-nav-btn"></div>
            ))}
          </div>
          <div className="skeleton skeleton-icon"></div>
        </div>
        <div className="outlet-image-section__content">
          <div className="skeleton skeleton-image"></div>
          <div className="image-info">
            <div className="skeleton skeleton-text skeleton-text--medium"></div>
            <div className="status-indicators">
              <div className="skeleton skeleton-text skeleton-text--small"></div>
              <div className="skeleton skeleton-text skeleton-text--small"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="outlet-image-section">
        <div className="outlet-image-section__header">
          <div className="image-navigation"></div>
          <img src={icon} alt="Visit Icon" />
        </div>
        <div className="error-state">
          <img src={SearchImage} alt="Error" className="error-image" />
          <h4 className="error-title">Error Fetching Data</h4>
          <p className="error-message">{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (!calls || calls.length === 0) {
    return (
      <div className="outlet-image-section">
        <div className="outlet-image-section__header">
          <div className="image-navigation"></div>
          <img src={icon} alt="Visit Icon" />
        </div>
        <div className="no-data-state">
          <img src={SearchImage} alt="No Data" className="error-image" />
          <p className="no-data-message">No visit images available</p>
        </div>
      </div>
    );
  }

  const currentCall = calls[selectedImageIndex] || {};
  const { outlet_image, end_time, outlet_status, qr_status } = currentCall;
  const outletStatusClass = getOutletStatusClass(outlet_status);
  const qrStatusClass = getQrStatusClass(qr_status);

  return (
    <div className="outlet-image-section">
      <div className="outlet-image-section__header">
        <div className="image-navigation">
          {calls.map((_, index) => (
            <button
              key={index}
              className={clsx("image-nav-btn", {
                active: index === selectedImageIndex,
              })}
              onClick={() => handleImageIndexChange(index)}
            >
              {index + 1}
            </button>
          ))}
        </div>
        <img src={icon} alt="Visit Icon" />
      </div>

      <div className="outlet-image-section__content">
        <div className="outlet-image-container">
          {imageLoading && (
            <div className="image-loading-overlay">
              <div className="image-loading-spinner"></div>
            </div>
          )}
          {imageError || !outlet_image ? (
            <div className="no-image-placeholder">
              <img src={SearchImage} alt="no" className="error-image" />
              <p className="no-image-text">No image found</p>
            </div>
          ) : (
            <img
              src={resolveMediaUrl(outlet_image)}
              alt="Shop"
              className={clsx("shop-image", { "image-loading": imageLoading })}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          )}
        </div>
        <div className="image-info">
          <div className="timestamp">{formattedTimestamp(end_time)}</div>
          <div className="status-indicators">
            {outlet_status && (
              <div className={clsx("status-dot", outletStatusClass)}>
                {renderStatusIcon(outletStatusClass)}
                {outlet_status || "Unknown Status"}
              </div>
            )}
            {qr_status && (
              <div className={clsx("status-dot", qrStatusClass)}>
                {renderStatusIcon(qrStatusClass)}
                {qr_status || "Unknown Status"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitCallImages;
